import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import * as S from './PageTopControls.styles';
import { PageTopControlsProps } from './PageTopControls.types';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { TOP_BANNER_HEIGHT } from '@/components/Banners/Banners.constants';
import { ANDROID_BANNER_HEIGHT } from '@/components/AndroidAppBanner/AndroidAppBanner.constants';
import { useIsTopBannerVisible } from '@/hooks/useIsTopBannerVisible';
import { useSearchStore } from '@/store/useSearchStore';
import { useAndroidBannersStore } from '@/store/useBannerStore/useAndroidBannerStore';
import useScreenSize from '@/hooks/useScreenSize';
import { useStickyHeaderOffset } from '@/hooks/useStickyHeaderOffset';

export const PageTopControls: React.FC<
  PropsWithChildren<PageTopControlsProps>
> = ({ children, onInitialRender }) => {
  const { isVisible, scrollPos, isTopOfPage } = useScrollVisibility();
  const controlsRef = useRef<HTMLDivElement>(null);
  useStickyHeaderOffset(controlsRef);
  const isTopBannerVisible = useIsTopBannerVisible();
  const isAndroidBannerVisible = useAndroidBannersStore(
    state => state.isAndroidBannerVisible
  );
  const isSuggestionsOpened = useSearchStore(
    state => state.isSuggestionsOpened
  );
  const [
    topControlsHeight,
    setTopControlsHeight,
    fixedTopControlsHeight,
    setFixedTopControlsHeight,
    canBeFixed,
    topControlsPosition,
    setTopControlsPosition,
  ] = usePageTopControlsStore(state => [
    state.topControlsHeight,
    state.setTopControlsHeight,
    state.fixedTopControlsHeight,
    state.setFixedTopControlsHeight,
    state.canBeFixed,
    state.topControlsPosition,
    state.setTopControlsPosition,
  ]);
  const { isWebView } = useBridgeState();
  const { isLarge } = useScreenSize();

  useEffect(() => {
    if (controlsRef.current) {
      if (topControlsPosition === 'fixed') {
        setFixedTopControlsHeight(controlsRef.current.offsetHeight);
      } else {
        setTopControlsHeight(controlsRef.current.offsetHeight);
      }
      onInitialRender?.(controlsRef.current.offsetHeight);
    }
  }, [isAndroidBannerVisible, scrollPos, topControlsPosition]);

  useEffect(() => {
    if (isLarge) {
      setTopControlsPosition('sticky');
    } else if (!isWebView) {
      const topControlsHeightFinal = isTopBannerVisible
        ? topControlsHeight + TOP_BANNER_HEIGHT
        : topControlsHeight;

      const topControlsHeightWithAndroid = isAndroidBannerVisible
        ? topControlsHeightFinal + ANDROID_BANNER_HEIGHT
        : topControlsHeightFinal;

      if (scrollPos >= topControlsHeightWithAndroid && canBeFixed) {
        setTopControlsPosition('fixed');
      }

      const isDynamicHeight = topControlsHeight !== fixedTopControlsHeight;

      const shouldChangePositionToRelative = isDynamicHeight
        ? scrollPos < topControlsHeightWithAndroid
        : isTopOfPage;

      if (shouldChangePositionToRelative) {
        setTopControlsPosition('relative');
      }
    } else if (isSuggestionsOpened) {
      setTopControlsPosition('relative');
    }
  }, [
    isLarge,
    isWebView,
    scrollPos,
    topControlsHeight,
    fixedTopControlsHeight,
    canBeFixed,
    isSuggestionsOpened,
  ]);

  return (
    <>
      {topControlsPosition === 'fixed' && (
        <S.PageTopControlsGutter $topControlsHeight={topControlsHeight} />
      )}
      <S.PageTopControlsBox
        ref={controlsRef}
        $isOverlapped={isSuggestionsOpened && !isLarge}
        $isVisible={isVisible || isSuggestionsOpened}
        $position={topControlsPosition}
        $fixedTopControlsHeight={fixedTopControlsHeight}
        data-lotriver-header
      >
        {children}
      </S.PageTopControlsBox>
    </>
  );
};
